import { getAuthoredOverview, renderAuthoredNarrativeBundle } from './authoredNarrativeRender';
import { shouldPublishAuthoredMethodology } from './authoredNarrativeLoader';
import { DGSTATS_COVERAGE_UNAVAILABLE_LABEL } from '../../services/solarDgStatsCoverage';
import {
  buildSolarInstallationNarrativeFacts,
  type SolarInstallationNarrativeFacts,
} from './solarInstallationsNarrativeFacts';

const CHART_ID = 'solar_installations';

export type SolarPvViewId =
  | 'capacity'
  | 'cost'
  | 'cumulative'
  | 'perCapita'
  | 'batteryCost'
  | 'installers'
  | 'installersCost';

export function solarNarrativeViewId(pvView: SolarPvViewId): string {
  return pvView === 'capacity' ? 'default' : pvView;
}

function fmtMoneyPerW(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toFixed(2);
}

function fmtMoneyPerKw(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toFixed(0);
}

function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toFixed(1);
}

export function buildSolarInstallationTokens(facts: SolarInstallationNarrativeFacts): Record<string, string> {
  if (facts.coverage.coverageStatus === 'UNAVAILABLE') {
    return {
      locationName: facts.locationName,
      latestDateOrYear: '—',
      latestYear: '—',
      solarCapacityMW: '—',
      trendSentence: DGSTATS_COVERAGE_UNAVAILABLE_LABEL,
      sectorShareSentence: '',
      coverageSentence: facts.coverage.coverageReason || DGSTATS_COVERAGE_UNAVAILABLE_LABEL,
      coverageNote: DGSTATS_COVERAGE_UNAVAILABLE_LABEL,
      localSolarKwPerResident: '—',
      aggregateSolarCostPerW: '—',
      representedProjectCount: '—',
      projectUnitLabel: facts.projectUnitLabel,
    };
  }

  const projectCountClause =
    facts.representedProjectCount != null
      ? ` across ${facts.representedProjectCount} ${facts.projectUnitLabel}`
      : '';

  return {
    locationName: facts.locationName,
    latestDateOrYear: facts.latestYear != null ? String(facts.latestYear) : '—',
    latestYear: facts.latestYear != null ? String(facts.latestYear) : '—',
    solarCapacityMW: facts.capacityMw != null ? facts.capacityMw.toFixed(2) : '—',
    trendSentence: facts.trendSentence,
    sectorShareSentence: facts.sectorShareSentence,
    coverageSentence: facts.coverageSentence,
    coverageNote: facts.coverageSentence,
    localSolarKwPerResident:
      facts.localSolarKwPerResident != null ? facts.localSolarKwPerResident.toFixed(3) : '—',
    solarPerCapitaTrendSentence: facts.solarPerCapitaTrendSentence,
    solarPerCapitaComparisonSentence: facts.solarPerCapitaComparisonSentence,
    cumulativeSolarMw: facts.cumulativeSolarMw != null ? facts.cumulativeSolarMw.toFixed(2) : '—',
    cumulativeGrowthSentence: facts.cumulativeGrowthSentence,
    latestAdditionSentence: facts.latestAdditionSentence,
    selectedSolarSectorLabel: facts.selectedSolarSectorLabel,
    aggregateSolarCostPerW: fmtMoneyPerW(facts.aggregateSolarCostPerW),
    localSolarCostPerW: fmtMoneyPerW(facts.aggregateSolarCostPerW),
    representedProjectCount:
      facts.representedProjectCount != null ? String(facts.representedProjectCount) : '—',
    projectUnitLabel: facts.projectUnitLabel,
    representedProjectCountClause: projectCountClause,
    validApplicationCount:
      facts.representedProjectCount != null ? String(facts.representedProjectCount) : '—',
    solarCostTrendSentence: facts.solarCostTrendSentence,
    solarCostComparisonSentence: facts.solarCostComparisonSentence,
    solarSampleSentence: facts.solarSampleSentence,
    ownershipMixSentence: facts.ownershipMixSentence,
    solarOnlyCostPerW: fmtMoneyPerKw(facts.solarOnlyCostPerKw),
    solarStorageCostPerW: fmtMoneyPerKw(facts.solarStorageCostPerKw),
    solarOnlyMeanCost: facts.solarOnlyCostPerKw != null ? `$${fmtMoneyPerKw(facts.solarOnlyCostPerKw)}` : '—',
    solarStorageMeanCost: facts.solarStorageCostPerKw != null ? `$${fmtMoneyPerKw(facts.solarStorageCostPerKw)}` : '—',
    solarOnlyMedianCostPerW: fmtMoneyPerKw(facts.solarOnlyCostPerKw),
    solarStorageMedianCostPerW: fmtMoneyPerKw(facts.solarStorageCostPerKw),
    actualUnit: facts.solarStorageCostUnitLabel,
    comparisonYear: facts.comparisonYear != null ? String(facts.comparisonYear) : '—',
    projectCostDifferenceSentence: facts.projectCostDifferenceSentence,
    sampleSentence: facts.sampleSentence,
    topInstallerLabel: facts.topInstallerLabel,
    topInstallerShare: fmtPct(facts.topInstallerShare),
    installerShareBasis: facts.installerShareBasis,
    secondInstallerSentence: facts.secondInstallerSentence,
    installerCountSentence: facts.installerCountSentence,
    lowestCostInstaller: facts.lowestCostInstaller,
    highestCostInstaller: facts.highestCostInstaller,
    lowestInstallerCostPerW: fmtMoneyPerW(facts.lowestInstallerCostPerW),
    highestInstallerCostPerW: fmtMoneyPerW(facts.highestInstallerCostPerW),
    minimumInstallerSample: String(facts.minimumInstallerSample),
    installerCostSampleSentence: facts.installerCostSampleSentence,
    perCapitaIntensityLabel: 'represented capacity per resident (kW DC per resident)',
    perCapitaKw: facts.localSolarKwPerResident != null ? facts.localSolarKwPerResident.toFixed(3) : '—',
    solarCutoffYear: facts.solarCutoffYear != null ? String(facts.solarCutoffYear) : '—',
    populationYear: facts.populationYear != null ? String(facts.populationYear) : '—',
    projectCount:
      facts.representedProjectCount != null ? String(facts.representedProjectCount) : '—',
    keyInsightsP25P75Sentence: '',
  };
}

export function buildSolarInstallationsAuthoredBundle(
  pvView: SolarPvViewId,
  facts: SolarInstallationNarrativeFacts
) {
  const tokens = buildSolarInstallationTokens(facts);
  const bundle = renderAuthoredNarrativeBundle(CHART_ID, solarNarrativeViewId(pvView), tokens);
  if (!shouldPublishAuthoredMethodology(bundle.authoringStatus)) {
    bundle.methodology = undefined;
  }
  return bundle;
}

export function solarInstallationsOverviewDescription(pvView: SolarPvViewId, fallback: string): string {
  return getAuthoredOverview(CHART_ID, solarNarrativeViewId(pvView), fallback);
}
